/**
 * <license header>
 */

import { Events } from '@adobe/aio-sdk';
import { CloudEvent } from 'cloudevents';
import { v4 as uuidv4 } from 'uuid';
import CustomLogger from '../custom-logger';
import type { PublishEventResult } from './types';

/**
 * PublishEvent component for Adobe I/O Event Publishing
 *
 * This component encapsulates the logic for publishing CloudEvents using Adobe I/O Events SDK.
 * It constructs CloudEvents with the provided payload and publishes them to the specified provider.
 *
 * @example
 * ```typescript
 * const publishEvent = new PublishEvent(
 *   'your-ims-org-id@AdobeOrg',
 *   'your-api-key',
 *   'your-access-token'
 * );
 *
 * // Publish an event
 * const result = await publishEvent.execute('provider-id', {
 *   type: 'commerce.order.created',
 *   data: {
 *     orderId: 'ORD-123456',
 *     customerId: 'CUST-789',
 *     amount: 199.99
 *   }
 * });
 * ```
 */
class PublishEvent {
  private readonly imsOrgId: string;
  private readonly apiKey: string;
  private readonly accessToken: string;
  private readonly customLogger: CustomLogger;

  /**
   * Creates a new PublishEvent instance
   *
   * @param imsOrgId - Adobe IMS Organization ID
   * @param apiKey - Adobe API Key (Client ID)
   * @param accessToken - Adobe Access Token
   * @param logger - Optional logger instance
   */
  constructor(imsOrgId: string, apiKey: string, accessToken: string, logger: any = null) {
    // Validate required parameters
    if (!imsOrgId?.trim()) {
      throw new Error('imsOrgId is required and cannot be empty');
    }
    if (!apiKey?.trim()) {
      throw new Error('apiKey is required and cannot be empty');
    }
    if (!accessToken?.trim()) {
      throw new Error('accessToken is required and cannot be empty');
    }

    this.imsOrgId = imsOrgId;
    this.apiKey = apiKey;
    this.accessToken = accessToken;
    this.customLogger = new CustomLogger(logger);

    this.customLogger.debug('PublishEvent initialized with valid configuration');
  }

  /**
   * Publishes a CloudEvent to Adobe I/O Events
   *
   * @param providerId - The Adobe I/O Events provider ID
   * @param eventCode - The event type identifier (e.g., 'commerce.order.created')
   * @param payload - The event payload data
   * @param subject - Optional subject for the event
   * @returns Promise<PublishEventResult> - The publish result
   *
   * @throws Error when providerId or eventCode is invalid or publishing fails
   */
  async execute(
    providerId: string,
    eventCode: string,
    payload: any,
    subject?: string
  ): Promise<PublishEventResult> {
    try {
      // Validate inputs
      if (!providerId?.trim()) {
        throw new Error('providerId is required and cannot be empty');
      }

      if (!eventCode?.trim()) {
        throw new Error('eventCode is required and cannot be empty');
      }

      if (payload === null || payload === undefined) {
        throw new Error('payload is required');
      }

      this.customLogger.info(`Publishing event to provider: ${providerId}`);

      // Generate unique event ID
      const eventId = uuidv4();

      // Construct CloudEvent using the cloudevents library
      const cloudEvent = new CloudEvent({
        id: eventId,
        source: `urn:uuid:${providerId}`,
        datacontenttype: 'application/json',
        type: eventCode,
        data: payload,
        ...(subject && { subject }),
      });

      this.customLogger.debug(`Constructed CloudEvent with ID: ${eventId}`);

      // Initialize Adobe I/O Events client
      const eventsClient = await Events.init(this.imsOrgId, this.apiKey, this.accessToken);

      this.customLogger.debug('Adobe I/O Events client initialized successfully');

      // Publish the event
      await eventsClient.publishEvent(cloudEvent);

      const publishedAt = new Date().toISOString();

      this.customLogger.info(`Event published successfully with ID: ${eventId}`);

      return {
        eventId,
        status: 'published',
        publishedAt,
      };
    } catch (error: any) {
      this.customLogger.error(`Failed to publish event: ${error.message}`);

      // Return error result instead of throwing
      return {
        eventId: uuidv4(), // Generate ID for tracking even failed events
        status: 'failed',
        publishedAt: new Date().toISOString(),
        error: error.message,
      };
    }
  }
}

export default PublishEvent;
